import React, { useEffect, useMemo, useRef, useState } from "react";

import {
    Box,
    Button,
    Container,
    IconButton,
    Modal,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import EventDisplay from "./EventDisplay";
import RoundedButton from "../RoundedButton";
import ToggleButtonTitled from "../ToggleButtonTitled";

import Icons from "../../helpers/icons";
import {
    weekDays,
    newDateUTC,
    getYearsBefore,
    months,
    dayjsUTC,
    formatDate,
    MIN_DATE,
    MAX_DATE,
} from "../../helpers/dates";

import { ITEM_HEIGHT } from "../FiltersReport";
import Colours from "../../helpers/colours";

const dayjs = dayjsUTC;

export const CALENDAR_MODE = "CALENDAR_MODE";

const eventPlacementHandler = (days, events) => {
    let eventsThroughMonth = {}; // Duplicated events for every day they're on

    let daysStrings = Object.keys(days);
    daysStrings.forEach((day, indexDay) => {
        // Takes only events from calendar days
        (events[day] ?? []).forEach((event) => {
            // For every event that happens in that day...

            event.FromDateString = dayjs(event.FromDate).format("YYYY-MM-DD");
            event.ToDateString = dayjs(event.ToDate).format("YYYY-MM-DD");

            let currentDate = event.FromDateString;
            let currentDateIndex = indexDay;
            while (
                currentDate !== event.ToDateString &&
                currentDateIndex < daysStrings.length
            ) {
                // ...Make it happen every day it lasts too(duplicate)

                if (!eventsThroughMonth.hasOwnProperty(currentDate)) {
                    eventsThroughMonth[currentDate] = [];
                }
                let auxEvent = { ...event };
                auxEvent["CurrentDateString"] = currentDate;
                eventsThroughMonth[currentDate].push(auxEvent);

                currentDateIndex++;
                currentDate = daysStrings[currentDateIndex] ?? "";
            }
            if (currentDate === event.ToDateString) {
                if (!eventsThroughMonth.hasOwnProperty(currentDate))
                    eventsThroughMonth[currentDate] = [];

                let auxEvent = { ...event };
                auxEvent["CurrentDateString"] = currentDate;
                eventsThroughMonth[currentDate].push(auxEvent);
            }
        });
    });

    const sortEventsDay = (eventsDay) =>
        eventsDay.sort((a, b) => {
            // Compare based on the "from" property (the oldest event first)
            if (a.FromDateString !== b.FromDateString) {
                return new Date(a.FromDateString) - new Date(b.FromDateString);
            }

            // If the events have the same from date, compare based on duration (to - from)
            const durationA =
                new Date(a.ToDateString) - new Date(a.FromDateString);
            const durationB =
                new Date(b.ToDateString) - new Date(b.FromDateString);

            return durationB - durationA; // Sort in descending order of duration
        });

    let eventsID = {};

    Object.values(events)
        .flat()
        .forEach((event) => {
            eventsID[`${event.EventID + event.FromDateString}`] = event;
        });

    // Place events in the calendar days
    daysStrings.forEach((day) => {
        if (eventsThroughMonth.hasOwnProperty(day)) {
            // 1º - Spot old(assigned) and new(unassigned) events for that day
            let assigned = [];
            let unassigned = [];
            eventsThroughMonth[day].forEach((event) => {
                if (
                    eventsID[
                        `${event.EventID + event.FromDateString}`
                    ].hasOwnProperty("space")
                ) {
                    if (new Date(day).getUTCDay() === 1) {
                        // Start of the week, renew spaces
                        delete eventsID[
                            `${event.EventID + event.FromDateString}`
                        ]["space"];
                        unassigned.push(event);
                    } else assigned.push(event);
                } else unassigned.push(event);
            });

            // 2º - Place assigned and initial spaces (start of the week not needed)

            // Total spaces with assigned and null spaces
            let initialDayLength =
                assigned.length > 0
                    ? Math.max(
                          ...assigned.map(
                              (event) =>
                                  eventsID[
                                      `${event.EventID + event.FromDateString}`
                                  ].space,
                          ),
                      )
                    : 1;

            // Fill the day total spaces with null values
            days[day] = new Array(initialDayLength).fill(null); // day = [null, null, null, null]

            // Place assigned events in their space
            assigned.forEach(
                (event) =>
                    (days[day][
                        eventsID[`${event.EventID + event.FromDateString}`]
                            .space - 1
                    ] = event),
            ); // day = [event, null, null, event]

            // 3º - Place unassigned

            // Sort new(unassigned)
            unassigned = sortEventsDay(unassigned);

            // Place unassigned
            while (unassigned.length > 0) {
                const id = unassigned[0].EventID + unassigned[0].FromDateString;
                const nullSpace = days[day].findIndex(
                    (place) => place === null,
                ); // Is there any empty space?

                if (nullSpace !== -1) {
                    // Yes
                    unassigned[0]["space"] = nullSpace + 1;
                    days[day][nullSpace] = unassigned.shift();
                    eventsID[id]["space"] = nullSpace + 1;
                } else {
                    // No
                    days[day].push(unassigned.shift());
                    const lastPlace = days[day].length;
                    days[day][lastPlace - 1]["space"] = lastPlace;
                    eventsID[id]["space"] = lastPlace;
                }
            }

            // Start of the week removes all blank spaces
            if (new Date(day).getUTCDay() === 1) {
                const removedNull = days[day].filter((place) => place !== null);

                const spaceAdjust = days[day].length - removedNull.length;

                const newDay = [];
                removedNull.forEach((event) => {
                    event.space -= spaceAdjust;
                    eventsID[`${event.EventID + event.FromDateString}`].space =
                        event.space;
                    newDay.push(event);
                });

                days[day] = newDay;
            }
        }
    });

    return days;
};

const ButtonField = ({ id, InputProps: { ref } = {}, setOpen, date }) => {
    return (
        <Box id="theRoundedButton">
            <RoundedButton
                ref={ref}
                id={id}
                onClick={() => setOpen?.((prev) => !prev)}
                component="button"
                sx={{
                    width: 255,
                    background: "none",
                    "&:hover": { backgroundColor: "whiteSmoke" },
                }}
            >
                <Typography
                    display="flex"
                    alignItems="center"
                    fontSize="1.3em"
                    sx={{ color: "black" }}
                >
                    {`${months[dayjs(date).month()]} ${dayjs(date).year()}`}
                </Typography>
            </RoundedButton>
        </Box>
    );
};

const CustomDateButton = ({ date, onChange = () => {} }) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box ref={anchorRef} component="div" display="inline">
                <DatePicker
                    timezone="UTC"
                    open={open}
                    views={["year", "month"]}
                    openTo="month"
                    minDate={MIN_DATE}
                    maxDate={MAX_DATE}
                    onChange={(newValue) => {
                        onChange(newValue);
                        setOpen(false);
                    }}
                    onClose={() => setOpen(false)}
                    onOpen={() => setOpen(true)}
                    value={date}
                    slots={{ field: ButtonField }}
                    slotProps={{
                        field: { setOpen, date },
                        popper: { anchorEl: anchorRef.current },
                    }}
                />
            </Box>
        </LocalizationProvider>
    );
};

// Isolated cell component: keeps hover state local so only this cell re-renders on mouse enter/leave
const CalendarDayCell = React.memo(function CalendarDayCell({
    date,
    dayEvents,
    isToday,
    currentMonth,
    dayIndex,
    hotelID,
    onEventClicked,
    onAddNew,
    setClickedDate,
    setOpenDay,
}) {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <Stack
            display={"flex"}
            style={{ width: "100%", height: "100%" }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/*  ____               */}
            {/* |  _ \  __ _ _   _  */}
            {/* | | | |/ _` | | | | */}
            {/* | |_| | (_| | |_| | */}
            {/* |____/ \__,_|\__, | */}
            {/*              |___/  */}

            <Button
                sx={{
                    textTransform: "none",
                    borderRadius: "50%",
                    minWidth: "28px",
                    width: "28px",
                    height: "28px",
                    bgcolor: isToday ? "primary.main" : "transparent",
                    color: isToday
                        ? "white"
                        : parseInt(date.slice(5, 7)) === currentMonth
                          ? "black"
                          : "gray",
                    "&:hover": {
                        bgcolor: isToday ? "primary.dark" : "rgba(0,0,0,0.08)",
                    },
                    cursor: "pointer",
                }}
                onClick={() => {
                    if (dayEvents.length > 0) {
                        setClickedDate(date);
                        setOpenDay((prev) => !prev);
                    } else {
                        onAddNew?.(date);
                    }
                }}
            >
                <Typography
                    fontWeight={
                        parseInt(date.slice(5, 7)) === currentMonth
                            ? "bold"
                            : ""
                    }
                >
                    {parseInt(date.slice(8, 10))}
                </Typography>
            </Button>

            {/*  _____                 _        */}
            {/* | ____|_   _____ _ __ | |_ ___  */}
            {/* |  _| \ \ / / _ \ '_ \| __/ __| */}
            {/* | |___ \ V /  __/ | | | |_\__ \ */}
            {/* |_____| \_/ \___|_| |_|\__|___/ */}
            <Stack
                direction={"column"}
                spacing={0.7}
                sx={{
                    width: "100%",
                    height: "80%",
                    paddingY: "0px",
                    marginY: "0px",
                }}
            >
                {dayEvents.length > 0 &&
                    dayEvents.map((event, eventNo) => {
                        if (eventNo === 3) {
                            return (
                                <Button
                                    key="more"
                                    onClick={() => {
                                        setClickedDate(date);
                                        setOpenDay((prev) => !prev);
                                    }}
                                    sx={{
                                        fontSize: "80%",
                                        textTransform: "none",
                                        cursor: "pointer",
                                        width: "100%",
                                        maxHeight: "16px",
                                        color: "black",
                                        ":hover": {
                                            backgroundColor: "lightgray",
                                        },
                                    }}
                                >
                                    {`+ ${
                                        dayEvents.filter(
                                            (e, index) =>
                                                e !== null && index >= 3,
                                        ).length
                                    } more`}
                                </Button>
                            );
                        } else if (event === null) {
                            return (
                                <EventDisplay
                                    key={eventNo}
                                    mode={"empty"}
                                    event={{
                                        CurrentDateString: date,
                                    }}
                                    hotelID={hotelID}
                                />
                            );
                        } else if (eventNo < 3) {
                            event.Style =
                                event.CurrentDateString ===
                                    event.FromDateString ||
                                new Date(
                                    event.CurrentDateString,
                                ).getUTCDay() === 1
                                    ? "start"
                                    : "";
                            event.Elevation = dayIndex * -1 + 7;

                            return (
                                <EventDisplay
                                    key={event.EventID ?? eventNo}
                                    mode={
                                        event.Style === "start"
                                            ? "line"
                                            : "empty"
                                    }
                                    hotelID={hotelID}
                                    event={event}
                                    onEventClicked={onEventClicked}
                                />
                            );
                        }
                        return null;
                    })}{" "}
                {dayEvents.length === 0 && isHovered && (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{ flex: 1, cursor: "pointer" }}
                        onClick={() => onAddNew?.(date)}
                    >
                        <Icons.Add
                            sx={{
                                color: "lightgrey",
                                fontSize: "2rem",
                            }}
                        />
                    </Stack>
                )}{" "}
            </Stack>
        </Stack>
    );
});

const EventsCalendar = ({
    hotelID,
    isMobile = false,
    events = [],
    onEventClicked = () => {},
    onAddNew = () => {},
    onToggle = () => {},
}) => {
    const eventsByDate = useMemo(() => {
        const grouped = {};
        events.forEach((event) => {
            const dateKey = dayjs(event.FromDate).format("YYYY-MM-DD");
            if (!grouped[dateKey]) grouped[dateKey] = [];
            grouped[dateKey].push(event);
        });
        return grouped;
    }, [events]);
    const [time, setTime] = useState(dayjs());

    const [clickedDate, setClickedDate] = useState("");
    const [openDay, setOpenDay] = useState(false);

    const [calendarDays, setCalendarDays] = useState({});

    const currentDate = dayjs(
        new Date().getUTCFullYear(),
        new Date().getUTCMonth(),
        1,
    );

    const handlePrevNextDateChange = (changeTo) => {
        const year = time.year();
        const month = time.month() + changeTo;

        const auxTime = dayjs().year(year).month(month).date(1);
        setTime(auxTime);
    };

    const backToCurrentDate = () => {
        setTime(currentDate);
    };

    const getMonthCalendarDays = () => {
        const year = time.year();
        const month = time.month();

        let auxDays = {};

        // Days of the previous month
        let firstWeekDay = newDateUTC(year, month, 1).getUTCDay();
        firstWeekDay = firstWeekDay === 0 ? 6 : firstWeekDay - 1; // week day correction so monday is 0 and sunday is 6
        for (let i = firstWeekDay - 1; i >= 0; i--) {
            const dateString = newDateUTC(year, month, i * -1)
                .toISOString()
                .slice(0, 10);

            auxDays[dateString] = [];
        }

        // Days of actual month
        let lastDay = newDateUTC(year, month + 1, 0).getUTCDate();
        for (let i = 1; i <= lastDay; i++) {
            const dateString = newDateUTC(year, month, i)
                .toISOString()
                .slice(0, 10);
            auxDays[dateString] = [];
        }

        // Days of the next month
        let lastWeekDay = newDateUTC(year, month + 1, 0).getUTCDay();
        new Date(Date.UTC(year, month + 1, 0)).getUTCDay();
        lastWeekDay = lastWeekDay === 0 ? 6 : lastWeekDay - 1; // week day correction so monday is 0 and sunday is 6
        for (let i = 1; i <= 6 - lastWeekDay; i++) {
            const dateString = newDateUTC(year, month + 1, i)
                .toISOString()
                .slice(0, 10);
            auxDays[dateString] = [];
        }

        if (Object.keys(eventsByDate).length > 0) {
            let eventsCopy = JSON.parse(JSON.stringify(eventsByDate));
            let auxCalendarEventsDays = eventPlacementHandler(
                auxDays,
                eventsCopy,
            );
            setCalendarDays(auxCalendarEventsDays);
        } else {
            setCalendarDays(auxDays);
        }
    };

    useEffect(() => {
        getMonthCalendarDays();
    }, [time, eventsByDate]);

    return (
        <Container maxWidth={false}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 2, mb: 2 }}
            >
                <Typography variant="h4">Events Calendar</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                    {(() => {
                        return (
                            <>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <ToggleButtonTitled
                                        TITLE={null}
                                        values={{
                                            list: {
                                                label: "List",
                                                icon: <Icons.List />,
                                            },
                                            calendar: {
                                                label: "Calendar",
                                                icon: <Icons.EventsCalendar />,
                                            },
                                        }}
                                        valueSet="calendar"
                                        onChange={(_, val) => onToggle(val)}
                                        size="small"
                                        ITEM_HEIGHT={ITEM_HEIGHT}
                                    />
                                </Box>
                                <Button
                                    variant="contained"
                                    sx={{
                                        height: ITEM_HEIGHT,
                                        color: "black",
                                        pl: 4,
                                        pr: 4,
                                        textTransform: "none",
                                    }}
                                    onClick={() => onAddNew()}
                                >
                                    <Icons.Add sx={{ paddingRight: 1 }} /> Add
                                    New
                                </Button>
                            </>
                        );
                    })()}
                </Stack>
            </Stack>

            <Paper elevation={1} sx={{ mt: 2, p: 2, borderRadius: 2 }}>
                <Stack
                    direction={"row"}
                    sx={{
                        justifyContent: "space-between",
                    }}
                >
                    <Stack direction="row" sx={{ paddingBottom: 2 }}>
                        <IconButton
                            onClick={() => handlePrevNextDateChange(-1)}
                        >
                            <Icons.ArrowLeft color={"black"} />
                        </IconButton>
                        <CustomDateButton
                            date={time}
                            onChange={(newValue) => {
                                setTime(dayjs(newValue));
                            }}
                        />

                        <IconButton
                            onClick={() => handlePrevNextDateChange(+1)}
                        >
                            <Icons.ArrowRight color={"black"} />
                        </IconButton>

                        {/* {time !== currentDate && (
                        <IconButton onClick={() => backToCurrentDate()}>
                            <Icons.Today color={"black"} />
                        </IconButton>
                    )} */}
                    </Stack>
                </Stack>

                {Object.keys(calendarDays).length > 0 && (
                    <>
                        <Table>
                            <TableBody>
                                <>
                                    {/** WEEKDAYS HEADER */}
                                    <TableRow
                                        sx={{
                                            display: "flex",
                                            height: "auto",
                                            width: "100%",
                                        }}
                                        key={"weekdays-row"}
                                    >
                                        {weekDays.map((weekDay) => {
                                            return (
                                                <TableCell
                                                    sx={{
                                                        justifyContent:
                                                            "center",
                                                        width: "14%",

                                                        border: "1px solid lightgrey",

                                                        backgroundColor:
                                                            Colours.tableRowBackground,
                                                        display: "flex",
                                                        paddingX: "1px",
                                                        paddingY: "1px",
                                                    }}
                                                    key={weekDay + "-cell"}
                                                >
                                                    <Typography>
                                                        {weekDay.slice(0, 3)}
                                                    </Typography>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>

                                    {[
                                        ...Array(
                                            Object.keys(calendarDays).length /
                                                7,
                                        ),
                                    ].map((row, rowIndex) => {
                                        return (
                                            <TableRow
                                                sx={{
                                                    display: "flex",
                                                    height: "120px",
                                                    width: "100%",
                                                }}
                                                key={rowIndex}
                                            >
                                                {Object.keys(calendarDays)
                                                    .slice(
                                                        rowIndex * 7,
                                                        (rowIndex + 1) * 7,
                                                    )
                                                    .map((date, dayIndex) => {
                                                        const isToday =
                                                            date ===
                                                            new Date()
                                                                .toISOString()
                                                                .slice(0, 10);
                                                        return (
                                                            <TableCell
                                                                sx={{
                                                                    width: "14%",
                                                                    border: "1px solid lightgrey",
                                                                    display:
                                                                        "flex",
                                                                    paddingX:
                                                                        "1px",
                                                                    paddingY:
                                                                        "1px",
                                                                }}
                                                                key={date}
                                                            >
                                                                <CalendarDayCell
                                                                    date={date}
                                                                    dayEvents={
                                                                        calendarDays[
                                                                            date
                                                                        ]
                                                                    }
                                                                    isToday={
                                                                        isToday
                                                                    }
                                                                    currentMonth={
                                                                        time.month() +
                                                                        1
                                                                    }
                                                                    dayIndex={
                                                                        dayIndex
                                                                    }
                                                                    hotelID={
                                                                        hotelID
                                                                    }
                                                                    onEventClicked={
                                                                        onEventClicked
                                                                    }
                                                                    onAddNew={
                                                                        onAddNew
                                                                    }
                                                                    setClickedDate={
                                                                        setClickedDate
                                                                    }
                                                                    setOpenDay={
                                                                        setOpenDay
                                                                    }
                                                                />
                                                            </TableCell>
                                                        );
                                                    })}
                                            </TableRow>
                                        );
                                    })}
                                </>
                            </TableBody>
                        </Table>
                        {/**
                                                                                   _       _ 
                                _   _ __ ___   ___  _ __ ___   _ __ ___   ___   __| | __ _| |
                              _| |_| '_ ` _ \ / _ \| '__/ _ \ | '_ ` _ \ / _ \ / _` |/ _` | |
                             |_   _| | | | | | (_) | | |  __/ | | | | | | (_) | (_| | (_| | |
                               |_| |_| |_| |_|\___/|_|  \___| |_| |_| |_|\___/ \__,_|\__,_|_|
                                                                                           */}
                        {openDay && clickedDate && (
                            <Modal
                                open={openDay}
                                onClose={() => setOpenDay(false)}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        width: 400,
                                        minHeight: "200px",
                                        bgcolor: "background.paper",
                                        borderRadius: "10px",
                                        boxShadow: 24,
                                        p: 4,
                                    }}
                                >
                                    <Stack
                                        alignItems={"center"}
                                        direction={"column"}
                                    >
                                        <Typography>
                                            {formatDate(clickedDate)}
                                        </Typography>
                                        <Stack
                                            direction={"column"}
                                            spacing={2}
                                            sx={{
                                                width: "100%",
                                                height: "100%",
                                                paddingY: "5px",
                                            }}
                                        >
                                            {calendarDays[clickedDate]
                                                .filter(
                                                    (event) => event !== null,
                                                )
                                                .map((event) => {
                                                    let eventStyled = {
                                                        ...event,
                                                    };
                                                    eventStyled.Style = "full";

                                                    return (
                                                        <EventDisplay
                                                            hotelID={hotelID}
                                                            event={eventStyled}
                                                            onEventClicked={(
                                                                ev,
                                                            ) => {
                                                                setOpenDay(
                                                                    false,
                                                                );
                                                                onEventClicked(
                                                                    ev,
                                                                );
                                                            }}
                                                        />
                                                    );
                                                })}
                                        </Stack>
                                    </Stack>
                                </Box>
                            </Modal>
                        )}
                    </>
                )}
            </Paper>
        </Container>
    );
};

export default EventsCalendar;

export const EventsCalendarButton = ({ isMobile = false, setMainMode }) => {
    return (
        <Button
            variant="contained"
            sx={{
                backgroundColor: "white",
                color: "black",
                border: "1px solid orange",
                borderRadius: 1000,
                "&:hover": {
                    backgroundColor: "orange",
                    color: "white",
                },
            }}
            disableElevation
            onClick={(v) => {
                setMainMode(CALENDAR_MODE);
            }}
        >
            <>
                <Icons.EventsCalendar sx={{ paddingRight: 1 }} />
                {isMobile ? "" : "Calendar"}
            </>
        </Button>
    );
};
